import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useGridApiContext } from '@mui/x-data-grid-pro';
import { CircularProgress, TextField, debounce } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { TypeAhead } from '@AMIEWEB/Common';
import {  getLocationStatesCitiesZip } from 'app/services/SharedServices/SharedServices';
import statesData from 'app/assets/jsons/State.json';
import { gridTypeAheadCellStyles } from './WorkExperienceGrid.style';
import { useSelector } from 'react-redux';
import { selectUser } from 'oidc/user.selectors';
import { userAllowedToEdit } from '../../helper';
import { disciplineDataSelector, specialtiesSelector } from 'store/redux-store/lookup/lookup.selectors';
import { handleGridCellChangeAndStopEdit } from './changeDetectionHelper';

enum CountryNames {
  USA = 'USA',
  CANADA = 'Canada',
}

const GridTypeAheadCell = React.memo(function GridTypeAheadCell(params: any) {
  const ref = useGridApiContext();
  const { id, value, field, row, colDef } = params;
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [cityOptions, setCityOptions] = useState<any[]>([]);
  const user = useSelector(selectUser);
  const specialtiesLookup = useSelector(specialtiesSelector);
  const disciplines = useSelector(disciplineDataSelector);

  const fieldName = {
    city: 'city',
    disciplineAbbr: 'disciplineAbbr',
    specialtyAbbr: 'specialtyAbbr',
    country: 'country',
    state: 'state',
    reasonForLeaving: 'reasonForLeaving',
    travelCompany: 'travelCompany',
  };

  const { classes } = gridTypeAheadCellStyles({
    columnWidth: colDef?.computedWidth,
    removeCloseIcon: field === fieldName.disciplineAbbr || field === fieldName.specialtyAbbr,
  });

  const getNewStateValue = useCallback(
    (newValue: string) => {
      if (field === fieldName.country && newValue) {
        const countryId: number = newValue === CountryNames.USA ? 1 : newValue === CountryNames.CANADA ? 2 : 0;
        if (newValue === CountryNames.USA || newValue === CountryNames.CANADA) {
          const statesList = statesData?.states.filter(e => e.CountryID === countryId).map(e => e.Description);
          return statesList[0] || '';
        }
      }
      return '';
    },
    [field, row],
  );

  const getOptions = useMemo(() => {
    if (field === fieldName.city) {
      return cityOptions;
    }
    if (field === fieldName.disciplineAbbr) {
      return (
        disciplines
          ?.map(item => ({ value: item?.object?.Abbreviation, label: item?.object?.Description }))
          ?.sort((a, b) => (b.label > a.label ? -1 : 1)) || []
      );
    }
    if (field === fieldName.specialtyAbbr) {
      const disciplineAbbr = row?.disciplineAbbr;
      const discipleData = disciplines?.find(item => item?.object?.Abbreviation === disciplineAbbr) ?? {};
      const specialityList = specialtiesLookup
        ?.filter(specialty => specialty?.object?.DisciplineID === discipleData?.object?.ID)
        ?.map(item => ({ value: item?.object?.Description, label: item?.object?.Description }));

      return specialityList?.sort((a, b) => (b.label > a.label ? -1 : 1)) || [];
    } else if (colDef?.valueOptions && typeof colDef?.valueOptions === 'function') {
      return colDef.valueOptions({ id, row, field });
    }
    return colDef?.valueOptions || [];
  }, [
    cityOptions,
    colDef,
    disciplines,
    field,
    fieldName.city,
    fieldName.disciplineAbbr,
    fieldName.specialtyAbbr,
    id,
    row,
    specialtiesLookup,
  ]);

  const getInputValue = useMemo(() => {
    const selectedObj = getOptions?.find(item => item.value === value);
    return selectedObj || value;
  }, [getOptions, value]);

  const handleOnChange = async (data: any) => {
    const newValue = data?.value;
    
    // Use the helper function for change detection and grid updates
    await handleGridCellChangeAndStopEdit({
      id,
      field,
      newValue,
      oldValue: value,
      apiRef: ref,
    });
    
    if (field === fieldName.disciplineAbbr) {
      await ref.current.setEditCellValue({ id: id, field: fieldName.specialtyAbbr, value: '' });
    } else if (field === fieldName.country) {
      const newStateValue = getNewStateValue(newValue);
      if (row?.isTimeOff) {
        await ref.current.setEditCellValue({ id: id, field: fieldName.state, value: '' });
      } else {
        await ref.current.setEditCellValue({ id: id, field: fieldName.state, value: newStateValue });
      }
      await ref.current.setEditCellValue({ id: id, field: fieldName.city, value: '' });
    } else if (field === fieldName.state) {
      await ref.current.setEditCellValue({ id: id, field: fieldName.city, value: '' });
    }
  };

  const fetchCities = async (country: string, state: string) => {
    const cityValues = await fetchCitiValues(country, state);
    setCityOptions(cityValues);
  };

  const fetchCitiValues = useCallback(
    async (country: string, state: string) => {
      if (country === CountryNames.USA && state) {
        setIsLoading(true);
        const result = await getLocationStatesCitiesZip({
          states: [state],
          cities: [],
        });
        let filteredCities = [];

        if (result?.length) {
          filteredCities = result[0]?.cities?.map(item => ({ id: item.id, value: item.name, label: item.name }));
          setIsLoading(false);
        }
        return filteredCities;
      } else {
        return [];
      }
    },
    [row?.country, row?.state],
  );

  useEffect(() => {
    if (params?.field === fieldName.city && row?.state) {
      const verifyValue = verifyInputCityValue(row?.city);
      if (verifyValue) {
        onTextSearch(row?.city.slice(0, 3), 'c');
      } else {
        fetchCities(row?.country, row?.state);
      }
    }
  }, [row?.state]);

  const handleOnTextChange = async event => {
    await ref.current.setEditCellValue({ id: id, field: field, value: event.target.value, debounceMs: 50 }, event);
    ref.current.stopCellEditMode({ id: id, field: field });
  };

  const onTextSearch = async (key: string, searchMode: 'c' | 'z') => {
    const selectedStates = row?.state ? [row?.state] : [];
    if (searchMode === 'c') {
      const result = await getLocationStatesCitiesZip({
        states: selectedStates,
        cities: [],
        searchMode: searchMode,
        searchValue: key,
      });
      if (result?.length) {
        let filteredCities = [];
        filteredCities = result[0]?.cities.map(item => {
          return { id: item.id, label: item.name, value: item.name };
        });
        setCityOptions(filteredCities);
      } else {
        setCityOptions([]);
      }
    }
  };

  const debouncedFetchData = useCallback(debounce(onTextSearch, 500), [row?.state]);

  const verifyInputCityValue = (value: string) => {
    if (value) {
      const firstLetter = value.charAt(0).toUpperCase();
      return firstLetter >= 'G';
    }
    return false;
  };

  const handleInputValueChange = value => {
    if (field === fieldName.city && value?.length >= 1) {
      const verifyValue = verifyInputCityValue(value);
      if (verifyValue) {
        debouncedFetchData(value, 'c');
      } else if (field === fieldName.city && value?.length == 0) {
        fetchCities(row?.country, row?.state);
      }
    }
  };

  const allowEdit = React.useMemo(() => {
    return userAllowedToEdit(user?.userInfo) || row?.allowEdit;
  }, [row?.allowEdit, user?.userInfo]);

  const isEditingDisabled = () => {
    if (
      row?.isTimeOff &&
      (field === fieldName.reasonForLeaving ||
        field === fieldName.disciplineAbbr ||
        field === fieldName.specialtyAbbr ||
        field === fieldName.travelCompany)
    ) {
      return true;
    }
    if (field === fieldName.disciplineAbbr || field === fieldName.specialtyAbbr) {
      return !allowEdit;
    }
    return false;
  };

  return (
    <>
      {(field === fieldName.state && !(row?.country === CountryNames.USA || row?.country === CountryNames.CANADA)) ||
      (field === fieldName.city && row?.country !== CountryNames.USA) ? (
        <TextField
          variant={'outlined'}
          size={'small'}
          onChange={handleOnTextChange}
          value={value}
          disabled={false}
          inputProps={{
            maxLength: row?.isTimeOff && (field === fieldName.city || field === fieldName.state) ? 100 : undefined,
          }}
        />
      ) : (
        <>
          {isLoading && field === fieldName.city ? (
            <CircularProgress size={20} sx={{ margin: 'auto' }} />
          ) : (
            <TypeAhead
              variant="standard"
              options={getOptions || []}
              value={getInputValue || ''}
              isDisabled={isEditingDisabled()}
              onChange={handleOnChange}
              onInputChange={handleInputValueChange}
              enableCustomScroll={true}
              removeCloseIcon={
                field === fieldName.disciplineAbbr || field === fieldName.specialtyAbbr || !value ? true : false
              }
              isCustomTheme={true}
              customClasses={{
                root: classes.typeAheadCellWrapper,
                inputRoot: classes.typeAheadCellInput,
                popper: classes.typeAheadCellPopper,
              }}
            />
          )}
        </>
      )}
    </>
  );
});

export const renderTypeAheadCell: GridColDef['renderCell'] = params => {
  return <GridTypeAheadCell {...params} />;
};
